import { register } from "./registry";
import { toast } from "./toast";

register((root) => {
  const form = root.querySelector<HTMLFormElement>("form[data-kit-doc-type='bilti']");
  if (!form || form.dataset.biltiBound === "1") return;
  form.dataset.biltiBound = "1";

  const itemTable = form.querySelector<HTMLTableElement>("table.kit-grid[data-kit-grid]");
  const vctsTable = form.querySelector<HTMLTableElement>("table#vcts-table");
  if (!itemTable) return;

  const itemTbody = itemTable.tBodies[0];
  if (!itemTbody) return;

  // --- 1. ITEM ROW & TOTALS CALCULATION ---
  const calcRow = (row: HTMLTableRowElement) => {
    const pkgsInput = row.querySelector<HTMLInputElement>("input[data-kit-col='packages']");
    const weightInput = row.querySelector<HTMLInputElement>("input[data-kit-col='weight']");
    const cftInput = row.querySelector<HTMLInputElement>("input[data-kit-col='cft']");
    const basisSelect = row.querySelector<HTMLSelectElement>("select[data-kit-col='rateBasis']");
    const rateInput = row.querySelector<HTMLInputElement>("input[data-kit-col='rate']");
    const freightInput = row.querySelector<HTMLInputElement>("input[data-kit-col='freight']");

    const pkgs = parseFloat(pkgsInput?.value || "0") || 0;
    const weight = parseFloat(weightInput?.value || "0") || 0;
    const cft = parseFloat(cftInput?.value || "0") || 0;
    const basis = (basisSelect?.value || "Pkg").toLowerCase();
    const rate = parseFloat(rateInput?.value || "0") || 0;

    let freight = 0;
    if (basis === "pkg") {
      freight = pkgs * rate;
    } else if (basis === "kg" || basis === "weight") {
      freight = weight * rate;
    } else if (basis === "fixed") {
      freight = rate;
    } else if (basis === "cft") {
      freight = cft * rate;
    } else {
      freight = pkgs * rate;
    }

    if (freightInput) {
      freightInput.value = freight.toFixed(2);
    }

    return { pkgs, weight, cft, freight };
  };

  const updateFormTotals = () => {
    let totPkgs = 0;
    let totWeight = 0;
    let totCft = 0;
    let totFreight = 0;

    Array.from(itemTbody.rows).forEach((row, idx) => {
      const snCell = row.cells[0];
      if (snCell) snCell.textContent = String(idx + 1);

      const { pkgs, weight, cft, freight } = calcRow(row);
      totPkgs += pkgs;
      totWeight += weight;
      totCft += cft;
      totFreight += freight;
    });

    // Update Item Grid Footers
    const pkgsFooter = document.getElementById("total-pkgs");
    const weightFooter = document.getElementById("total-weight");
    const cftFooter = document.getElementById("total-cft");
    const freightFooter = document.getElementById("grid-freight-total");
    const summaryFreight = document.getElementById("summary-freight") as HTMLInputElement | null;

    if (pkgsFooter) pkgsFooter.textContent = String(totPkgs);
    if (weightFooter) weightFooter.textContent = totWeight > 0 ? totWeight.toFixed(2) : "0";
    if (cftFooter) cftFooter.textContent = totCft > 0 ? totCft.toFixed(2) : "0";
    if (freightFooter) freightFooter.textContent = totFreight.toFixed(2);
    if (summaryFreight) summaryFreight.value = totFreight.toFixed(2);

    // Auto-update VCTS Row 1 defaults (Qty & Goods Desc)
    const vctsRow1 = vctsTable?.tBodies[0]?.rows[0];
    if (vctsRow1) {
      const vctsQty = vctsRow1.querySelector<HTMLInputElement>("input[name*='.Qty']");
      if (vctsQty && (vctsQty.value === "0" || !vctsQty.dataset.userEdited)) {
        vctsQty.value = String(totPkgs);
      }

      const firstDescInput = itemTbody.rows[0]?.querySelector<HTMLInputElement>("input[data-kit-col='description']");
      const vctsDesc = vctsRow1.querySelector<HTMLInputElement>("input[name*='.GoodsDesc']");
      if (vctsDesc && firstDescInput && (!vctsDesc.dataset.userEdited || vctsDesc.value === "")) {
        vctsDesc.value = firstDescInput.value;
      }
    }

    // Evaluate Charge formulas: F<rate> or V<rate>
    const goodsValueInput = form.querySelector<HTMLInputElement>("input[name='Form.GoodsValue']");
    const goodsVal = parseFloat(goodsValueInput?.value || "0") || 0;

    for (let i = 1; i <= 7; i++) {
      const chargeInput = form.querySelector<HTMLInputElement>(`input[name='Form.Charge${i}']`);
      if (chargeInput) {
        const formula = chargeInput.dataset.formula;
        if (formula) {
          if (formula.startsWith("F") || formula.startsWith("f")) {
            const r = parseFloat(formula.slice(1)) || 0;
            chargeInput.value = (r * totFreight).toFixed(2);
          } else if (formula.startsWith("V") || formula.startsWith("v")) {
            const r = parseFloat(formula.slice(1)) || 0;
            chargeInput.value = (r * goodsVal).toFixed(2);
          }
        }
      }
    }
  };

  // Initial calculation on load
  updateFormTotals();

  // Automatically recalculate whenever rows are added or removed in itemTbody
  const observer = new MutationObserver(() => {
    updateFormTotals();
  });
  observer.observe(itemTbody, { childList: true });

  // Listen to inputs within item grid
  itemTable.addEventListener("input", (e) => {
    const target = e.target as HTMLElement;
    if (target.matches("input[data-kit-col], select[data-kit-col]")) {
      updateFormTotals();
    }
  });

  itemTable.addEventListener("change", (e) => {
    const target = e.target as HTMLElement;
    if (target.matches("input[data-kit-col], select[data-kit-col]")) {
      updateFormTotals();
    }
  });

  // Listen to Goods Value input
  const goodsValInput = form.querySelector<HTMLInputElement>("input[name='Form.GoodsValue']");
  goodsValInput?.addEventListener("input", updateFormTotals);

  // --- 2. DELETE ALL ITEMS ---
  const delAllItemsBtn = form.querySelector<HTMLElement>("[data-kit-grid-del-all], .cb-btn-grid-del-all");
  delAllItemsBtn?.addEventListener("click", (e) => {
    e.preventDefault();
    while (itemTbody.rows.length > 1) {
      itemTbody.lastElementChild?.remove();
    }
    const r = itemTbody.rows[0];
    if (itemTbody.rows.length === 1 && r) {
      r.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input, select").forEach((inp) => {
        if (inp instanceof HTMLInputElement) {
          if (inp.dataset.kitCol === "packages") inp.value = "1";
          else inp.value = "";
        } else if (inp instanceof HTMLSelectElement) {
          inp.selectedIndex = 0;
        }
      });
      calcRow(r);
    }
    updateFormTotals();
    form.dispatchEvent(new Event("input", { bubbles: true }));
  });

  // --- 2b. PARTY RATE (CargoBooks level, M-12): goods + unit + charge-by look up the party rate for the parties, pay mode,
  // places and miti; a rate found fills the Rate box and locks it (the save checks the same rule), and when the rate says so
  // its ST and Thela charges fill heads 1 and 2. An expired rate only warns (OD-03).
  const field = (name: string) => form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name='${name}']`)?.value.trim() ?? "";
  const resolveRate = async (row: HTMLTableRowElement) => {
    const rateInput = row.querySelector<HTMLInputElement>("input[data-kit-col='rate']");
    if (!rateInput) return;
    const unlock = () => {
      if (!rateInput.dataset.partyRate) return;
      rateInput.readOnly = false;
      delete rateInput.dataset.partyRate;
    };
    const q: Record<string, string> = {
      consignor_id: field("Form.ConsignorId"), consignee_id: field("Form.ConsigneeId"), pay_mode: field("Form.PayMode").replace(/\s+/g, ""),
      from: field("Form.FromPlaceId"), to: field("Form.ToPlaceId"), miti: field("Form.Miti"),
      description_id: row.querySelector<HTMLInputElement>("input[name$='.DescriptionId']")?.value ?? "",
      unit_id: row.querySelector<HTMLSelectElement>("select[data-kit-col='unit']")?.value ?? "",
      rate_basis: row.querySelector<HTMLSelectElement>("select[data-kit-col='rateBasis']")?.value ?? "Pkg",
    };
    if (Object.entries(q).some(([k, v]) => k !== "consignee_id" && !v)) { unlock(); return; }
    let data: { expired?: boolean; validUpto?: string; rate?: number; stCharge?: number; thelaCharge?: number; changeAmount?: boolean; changeAmountApplicable?: boolean };
    try {
      const r = await fetch(`/api/v1/party-rates/resolve?${new URLSearchParams(q).toString()}`);
      if (!r.ok) { unlock(); return; }
      data = await r.json();
    } catch { unlock(); return; }
    if (data.expired) { unlock(); toast("warn", `Party rate expired on ${data.validUpto ?? "its date"}; type the rate`); return; }
    if (data.rate === undefined) { unlock(); return; }
    rateInput.value = Number(data.rate).toFixed(2);
    rateInput.readOnly = true;
    rateInput.dataset.partyRate = "1";
    if (data.changeAmount && data.changeAmountApplicable) {
      const st = form.querySelector<HTMLInputElement>("input[name='Form.Charge1']");
      const thela = form.querySelector<HTMLInputElement>("input[name='Form.Charge2']");
      if (st) st.value = Number(data.stCharge ?? 0).toFixed(2);
      if (thela) thela.value = Number(data.thelaCharge ?? 0).toFixed(2);
    }
    updateFormTotals();
  };
  const resolveAll = () => Array.from(itemTable.tBodies[0]?.rows ?? []).forEach((row) => void resolveRate(row));
  itemTable.addEventListener("kit:accepted", (e) => {
    const row = (e.target as HTMLElement).closest("tr");
    if (row) void resolveRate(row);
  });
  itemTable.addEventListener("change", (e) => {
    const target = e.target as HTMLElement;
    const row = target.closest("tr");
    if (row && target.matches("select[data-kit-col='unit']")) {
      // As CargoBooks, the unit decides how freight is charged: a KG/weight unit by weight, FIXED as a fixed amount, others per package.
      const unitName = (target as HTMLSelectElement).selectedOptions[0]?.text.trim().toUpperCase() ?? "";
      const basis = row.querySelector<HTMLSelectElement>("select[data-kit-col='rateBasis']");
      if (basis) basis.value = /^(KG|KGS|WEIGHT)$/.test(unitName) ? "Kg" : unitName === "FIXED" ? "Fixed" : unitName === "CFT" ? "Cft" : "Pkg";
      updateFormTotals();
    }
    if (row && target.matches("select[data-kit-col='unit'], select[data-kit-col='rateBasis']")) void resolveRate(row);
  });
  // The parties, pay mode and places decide which rate applies: a change re-checks every row.
  form.addEventListener("kit:accepted", (e) => {
    if (!itemTable.contains(e.target as Node)) resolveAll();
  });
  form.querySelector("[name='Form.PayMode']")?.addEventListener("change", resolveAll);

  // --- 2c. The consignor's default pay mode becomes the bilti's (as CargoBooks, T-B7); the change re-runs the Bill To rule.
  const payModeSelect = form.querySelector<HTMLSelectElement>("[name='Form.PayMode']");
  form.querySelector("[name='Form.ConsignorText']")?.addEventListener("kit:accepted", (e) => {
    const mode = ({ ToPay: "To Pay", Paid: "Paid", Due: "Due" } as Record<string, string>)[(e as CustomEvent<{ payMode?: string }>).detail?.payMode ?? ""];
    if (!payModeSelect || !mode) return;
    payModeSelect.value = mode;
    payModeSelect.dispatchEvent(new Event("change", { bubbles: true }));
  });
  form.querySelector("[name='Form.ConsigneeText']")?.addEventListener("kit:accepted", () => {
    payModeSelect?.dispatchEvent(new Event("change", { bubbles: true }));
  });

  // --- 3. CONSIGNOR & CONSIGNEE AUTOPAIRING TO VCTS & BILL TO ---
  const consignorInput = form.querySelector<HTMLInputElement>("input[name='Form.ConsignorText']");
  consignorInput?.addEventListener("blur", () => {
    const name = consignorInput.value.trim();
    if (!name) return;

    // Set VCTS Row 1 Supplier Name
    const vctsFirst = vctsTable?.tBodies[0]?.rows[0];
    if (vctsFirst) {
      const supInput = vctsFirst.querySelector<HTMLInputElement>("input[name*='.SupplierName']");
      if (supInput && !supInput.value) {
        supInput.value = name;
      }
    }
  });

  // Track manual edits to VCTS fields so auto-defaults do not overwrite
  vctsTable?.addEventListener("input", (e) => {
    const target = e.target as HTMLInputElement;
    if (target.matches("input[name*='.Qty'], input[name*='.GoodsDesc']")) {
      target.dataset.userEdited = "1";
    }
  });
});
